import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { ApiKey } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

export type ApiKeyStatus = 'active' | 'revoked';

export const apiKeyStatus = (key: ApiKey): ApiKeyStatus =>
  key.revokedAt === null ? 'active' : 'revoked';

/** Column model for the API keys table. Search covers name, prefix, scopes and origins. */
export function useApiKeyColumns() {
  const { t } = useTranslation('apiKeys');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<ApiKey>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('columns.name'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-foreground font-medium">{row.original.name}</span>
            <code className="text-muted-foreground text-xs">{row.original.prefix}…</code>
          </div>
        ),
      },
      {
        id: 'prefix',
        accessorKey: 'prefix',
        header: t('columns.prefix'),
        enableSorting: false,
      },
      {
        id: 'kind',
        accessorKey: 'kind',
        header: t('columns.kind'),
        enableSorting: false,
        meta: { filterKey: 'kind', searchable: false },
        cell: ({ row }) => <Badge variant="outline">{t(`kind.${row.original.kind}`)}</Badge>,
      },
      {
        id: 'scopes',
        accessorKey: 'scopes',
        header: t('columns.scopes'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.scopes.length === 0 ? (
            <span className="text-sm">{t('allScopes')}</span>
          ) : (
            <ul
              className="flex max-w-72 flex-wrap gap-1 whitespace-normal"
              aria-label={t('columns.scopes')}
            >
              {row.original.scopes.map((scope) => (
                <li key={scope}>
                  <Badge variant="secondary" className="font-mono text-[11px]">
                    {scope}
                  </Badge>
                </li>
              ))}
            </ul>
          ),
      },
      {
        id: 'origins',
        accessorKey: 'allowedOrigins',
        header: t('columns.origins'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.kind === 'secret' ? (
            <span className="text-muted-foreground text-xs">{t('serverOnly')}</span>
          ) : (
            <ul className="flex flex-col gap-0.5 font-mono text-xs">
              {row.original.allowedOrigins.map((origin) => (
                <li key={origin}>{origin}</li>
              ))}
            </ul>
          ),
      },
      {
        id: 'lastUsedAt',
        accessorFn: (row) => row.lastUsedAt ?? undefined,
        header: t('columns.lastUsed'),
        sortUndefined: 'last',
        meta: { sortField: 'lastUsedAt', searchable: false },
        cell: ({ row }) =>
          row.original.lastUsedAt ? (
            <span className="text-sm" title={format.utc(row.original.lastUsedAt)}>
              {format.relative(row.original.lastUsedAt)}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">{t('neverUsed')}</span>
          ),
      },
      {
        id: 'status',
        accessorFn: apiKeyStatus,
        header: t('columns.status'),
        enableSorting: false,
        meta: { filterKey: 'status', searchable: false },
        cell: ({ row }) =>
          row.original.revokedAt ? (
            <Badge
              variant="outline"
              className="text-muted-foreground"
              title={format.utc(row.original.revokedAt)}
            >
              {t('status.revoked')}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-success/15 text-success dark:bg-success/20 border-transparent"
            >
              {t('status.active')}
            </Badge>
          ),
      },
    ],
    [format, t],
  );
}
